import {
  Button,
  FormControl,
  HorizontalStack,
  Input,
  Text,
  Textarea,
  VerticalStack,
} from '@lego/klik-ui';
import { type ChangeEvent, type FormEvent, useEffect, useRef, useState } from 'react';
import { FileUploadButton } from '@/components/common/FileUploadButton';
import {
  type SubmitStatus,
  SubmitStatusIndicator,
} from '@/components/common/SubmitStatusIndicator';
import { useAddSshKeys } from '@/hooks/device_ssh_keys';
import { MAX_EXPIRY_HOURS, validateSshPublicKey } from '@/utility/sshKeyValidation';

type Props = {
  deviceId: number;
  onSuccess: () => void;
  onCancel: () => void;
};

const SUCCESS_DELAY_MS = 1500;

const validatePubFile = (file: File): string | null =>
  file.name.endsWith('.pub') ? null : 'Please upload a public key file (.pub)';

export const AddSshKeyForm = ({ deviceId, onSuccess, onCancel }: Props) => {
  const [publicKey, setPublicKey] = useState('');
  const [expiresInHours, setExpiresInHours] = useState(24);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  const addSshKey = useAddSshKeys(deviceId);
  const successTimeoutRef = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    };
  }, []);

  const handleFileRead = (content: string, name: string) => {
    setPublicKey(content);
    setFileName(name);
    setValidationError(null);
  };

  const handleSubmit = async (e?: FormEvent) => {
    e?.preventDefault();
    setValidationError(null);
    setSubmitError(null);

    const validation = validateSshPublicKey(publicKey, expiresInHours);
    if (validation.error !== null) {
      setValidationError(validation.error);
      return;
    }

    setSubmitStatus('submitting');

    try {
      await addSshKey({ publicKey: validation.key, expiresAt: validation.expiresAt });
      setSubmitStatus('success');
      successTimeoutRef.current = setTimeout(onSuccess, SUCCESS_DELAY_MS);
    } catch {
      setSubmitStatus('error');
      setSubmitError('Failed to add SSH key');
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <VerticalStack align="stretch" spacing={4}>
        <FormControl isInvalid={!!validationError}>
          <FormControl.Label>Public Key</FormControl.Label>
          <Textarea
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
              setPublicKey(e.target.value);
              setFileName(null);
              setValidationError(null);
            }}
            placeholder="ssh-ed25519 AAAA... or ssh-rsa AAAA..."
            rows={4}
            value={publicKey}
          />
          <Text fontSize="sm">Paste your public key or upload a .pub file</Text>
          {validationError && <FormControl.ErrorText>{validationError}</FormControl.ErrorText>}
        </FormControl>

        <FormControl>
          <FileUploadButton
            accept=".pub"
            label={fileName ?? 'Upload .pub file'}
            onError={setValidationError}
            onFileRead={handleFileRead}
            validate={validatePubFile}
          />
        </FormControl>

        <FormControl>
          <FormControl.Label>Expires in (hours)</FormControl.Label>
          <Input
            max={MAX_EXPIRY_HOURS}
            min={1}
            onChange={(e) => setExpiresInHours(Number(e.target.value))}
            type="number"
            value={expiresInHours}
          />
          <Text fontSize="sm">Maximum {MAX_EXPIRY_HOURS} hours (security policy)</Text>
        </FormControl>

        <SubmitStatusIndicator
          errorMessage={submitError}
          label="SSH Key"
          onReset={() => {
            setSubmitStatus('idle');
            setSubmitError(null);
          }}
          onRetry={() => void handleSubmit()}
          status={submitStatus}
          submittingText="Adding key to device..."
          successText="SSH key added successfully"
        />

        <HorizontalStack justify="flex-end" spacing={3}>
          <Button
            isDisabled={submitStatus === 'submitting' || submitStatus === 'success'}
            onClick={onCancel}
            variant="ghost"
          >
            Cancel
          </Button>
          <Button
            isDisabled={submitStatus === 'submitting' || submitStatus === 'success'}
            isLoading={submitStatus === 'submitting'}
            type="submit"
          >
            Add SSH Key
          </Button>
        </HorizontalStack>
      </VerticalStack>
    </form>
  );
};
